import { useMutation } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import { getCircuitForImport } from '@/api/circuit-import.api';
import { useCallback } from 'react';
import { createCircuitDay } from '@/utils/circuits/createEmptyCircuitDays';
import { toast } from 'react-toastify';
import { toastOptions } from '@/hooks/useToast';
import { parseSteps } from '@/utils/circuits/loadCircuitDefaultValues';
import {
  BenefitFormData,
  CircuitDayFormData,
} from '@/types/circuits/CircuitDaysFormData';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import { useFormContext } from 'react-hook-form';
import { LOCALES, useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';

export const useImportCircuitDay = () =>
  useMutation({
    mutationFn: ({
      circuitCode,
      dayNumber,
    }: {
      circuitCode: number;
      dayNumber: number;
    }) => getCircuitForImport(circuitCode, dayNumber),
  });

type fieldsType = keyof Omit<
  CircuitDayFormData['steps'][number]['transport'],
  'standardBenefit' | 'optionalBenefits'
>;
const ALL_FIELDS: fieldsType[] = [
  'id',
  'nbDays',
  'time',
  'standardDisplays',
  'optionalDisplays',
  'indexFirstDay',
];

export const useOnImportCircuitDay = ({
  index,
  daysLength,
  addBenefits,
  setDisplayedDayIndex,
}: {
  index: number;
  daysLength: number;
  addBenefits: (props: {
    standardBenefit: BenefitFormData;
    optionalBenefits: BenefitFormData[];
    type: CircuitBenefitType;
    stepIndex: number;
    stepNbDays?: number;
    stepId: string;
    isModification: boolean;
  }) => void;
  setDisplayedDayIndex: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { setValue } = useFormContext<CircuitFormDataWithTranslations>();
  const { mutate: importCircuitDay, isPending } = useImportCircuitDay();
  const { t } = useTranslation();
  const onImportCircuitDay = useCallback(
    (circuitCode: number, dayNumber: number) => {
      importCircuitDay(
        { circuitCode, dayNumber },
        {
          onSuccess: (result) => {
            const stepTitle = result.stepTitle ?? undefined;
            const description = result.description ?? undefined;

            // Base fields (persisted on CircuitDay) + translation fields
            // (what StyledTranslatableInput/Textarea actually display).
            setValue(`circuitDays.${index}.stepTitle`, stepTitle);
            setValue(`circuitDays.${index}.description`, description);
            for (const locale of LOCALES) {
              setValue(
                `translations.${locale}.circuitDays.${index}.stepTitle`,
                stepTitle ?? '',
              );
              setValue(
                `translations.${locale}.circuitDays.${index}.description`,
                description ?? '',
              );
            }
            setValue(`circuitDays.${index}.images`, result.images ?? []);

            const steps = parseSteps(
              result.CircuitDayStep.map((step) => ({
                ...step,
                indexFirstDay: index,
                nbDays: Math.min(step.nbDays ?? 1, daysLength - index),
              })),
              undefined,
              t,
            );

            steps.forEach((step, stepIndex) => {
              setValue(
                `circuitDays.${index}.steps.${stepIndex}`,
                createCircuitDay().steps[0],
              );

              const addStep = (
                benefitType: keyof CircuitDayFormData['steps'][number],
                circuitBenefitType: CircuitBenefitType,
              ) => {
                if (
                  step[benefitType]?.standardBenefit ||
                  step[benefitType]?.optionalBenefits.length > 0
                ) {
                  // Assign a fresh unique id to each imported step. The step id
                  // is only used client-side as a dedup key (tempId) in
                  // parseCircuitDaysToSubmit. Reusing the source day's DB id
                  // makes an imported step collide with the source day when
                  // importing within the same circuit, so it gets silently
                  // dropped from the save payload. addBenefits/addMultiDayBenefits
                  // replicate multi-day steps using this same id, so anchor and
                  // replicas stay coherent under the new id.
                  const newStepId = uuid();
                  const fieldsMapper = ALL_FIELDS.reduce(
                    (acc, field) => {
                      acc[field] =
                        field === 'indexFirstDay'
                          ? index
                          : field === 'id'
                            ? newStepId
                            : step[benefitType][field as fieldsType];
                      return acc;
                    },
                    {} as Record<
                      fieldsType,
                      (typeof step)[typeof benefitType][fieldsType] | number
                    >,
                  );
                  Object.entries(fieldsMapper).forEach(([key, value]) => {
                    setValue(
                      `circuitDays.${index}.steps.${stepIndex}.${benefitType}.${key as fieldsType}`,
                      value,
                    );
                  });
                  addBenefits({
                    standardBenefit: step[benefitType]
                      .standardBenefit as BenefitFormData,
                    optionalBenefits: step[benefitType]
                      .optionalBenefits as BenefitFormData[],
                    type: circuitBenefitType,
                    stepIndex,
                    stepNbDays: step[benefitType].nbDays,
                    stepId: newStepId,
                    isModification: false,
                  });
                }
              };

              addStep('transport', 'Transport');
              addStep('meals', 'Repas');
              addStep('activities', 'Activités');
              addStep('accommodations', 'Hébergements');
            });

            setDisplayedDayIndex((prev) => prev - 1);
            setTimeout(() => {
              setDisplayedDayIndex(index);
            }, 0);

            toast.success('Étape importée avec succès', toastOptions);
          },
          onError: (error) => {
            toast.error(error.message, toastOptions);
          },
        },
      );
    },
    [
      addBenefits,
      daysLength,
      importCircuitDay,
      index,
      setDisplayedDayIndex,
      setValue,
      t,
    ],
  );

  return {
    onImportCircuitDay,
    isPending,
  };
};
